/**
 * kmlRenderer.ts
 * -----------------------------------------------------------------------------
 * Shared Google Maps JS API renderer for parsed KML (see kmlParser.ts).
 * Consumed by both screens/Monitors/MapView.tsx and
 * screens/MonitorOmega/MapViewOmega.tsx so a KML feature only needs to be
 * wired up once for it to appear consistently on every map screen.
 *
 * Every function here is imperative (creates native maps.* objects and pushes
 * them into the caller-supplied `overlaysRef`), matching the existing
 * ref-based cleanup pattern already used by the map screens.
 * -----------------------------------------------------------------------------
 */

import type {
  KmlFolder,
  KmlGeometry,
  KmlGroundOverlay,
  KmlOverlay,
  KmlParseResult,
  KmlPhotoOverlay,
  KmlPlacemarkProperties,
  KmlScreenOverlay,
  KmlStyle,
  LatLngAlt,
} from "./kmlParser";

type MutableRef<T> = { current: T };

/**
 * Splits a kmlParser "#RRGGBBAA" color string into the {hex, opacity} shape
 * the Google Maps JS API expects (strokeColor/fillColor is "#RRGGBB",
 * strokeOpacity/fillOpacity is a separate 0-1 float).
 */
export function splitKmlColor(
  color: string | null | undefined,
  fallbackHex = "#FF0000",
  fallbackOpacity = 1
): { hex: string; opacity: number } {
  if (!color || color.length !== 9) {
    return { hex: fallbackHex, opacity: fallbackOpacity };
  }
  const hex = color.substring(0, 7);
  const alphaHex = color.substring(7, 9);
  const opacity = parseInt(alphaHex, 16) / 255;
  return { hex, opacity: Number.isNaN(opacity) ? fallbackOpacity : opacity };
}

/** Converts a kmlParser LatLngAlt[] array into google.maps.LatLng-friendly literals. */
export function toLatLngLiterals(coords: LatLngAlt[]) {
  return coords.map((c) => ({ lat: c.lat, lng: c.lng }));
}

/**
 * Builds InfoWindow HTML for a placemark, honoring <BalloonStyle> when present
 * (substituting the KML $[name]/$[description] placeholders and applying
 * bgColor/textColor). Returns null when there is nothing to show, or when
 * balloonStyle.displayMode is "hide".
 */
function buildBalloonHtml(
  properties: KmlPlacemarkProperties,
  style: KmlStyle | null
): string | null {
  const balloon = style && style.balloonStyle;
  if (balloon && balloon.displayMode === "hide") return null;

  const name = properties.name || "";
  const description = properties.description || "";

  if (balloon && balloon.text) {
    const html = balloon.text
      .split("$[name]")
      .join(name)
      .split("$[description]")
      .join(description);
    const bg = balloon.bgColor ? splitKmlColor(balloon.bgColor).hex : "#FFFFFF";
    const color = balloon.textColor ? splitKmlColor(balloon.textColor).hex : "#000000";
    return `<div style="background:${bg};color:${color};padding:8px;">${html}</div>`;
  }

  if (!name && !description) return null;
  return `<strong>${name}</strong><div>${description}</div>`;
}

/**
 * Resolves an <IconStyle> into Maps JS Marker `icon`/`label` options.
 * Best-effort only: bitmap icons can't be rotated or color-tinted through the
 * Marker API, so IconStyle.heading and IconStyle.color are intentionally not
 * applied (documented limitation, not a bug).
 */
// function buildMarkerIcon(maps: any, style: KmlStyle | null): any {
//   const iconStyle = style && style.iconStyle;
//   if (!iconStyle || !iconStyle.iconHref) return undefined;

//   const baseSize = 32; // Google Earth's default placemark icon size
//   const scale = iconStyle.scale || 1;
//   const size = Math.max(1, Math.round(baseSize * scale));

//   const icon: any = {
//     url: iconStyle.iconHref,
//     scaledSize: new maps.Size(size, size),
//   };

//   if (iconStyle.hotSpot) {
//     const hs = iconStyle.hotSpot;
//     const anchorX =
//       hs.xunits === "fraction" ? hs.x * size : hs.xunits === "insetPixels" ? size - hs.x : hs.x;
//     const anchorYFromBottom =
//       hs.yunits === "fraction" ? hs.y * size : hs.yunits === "insetPixels" ? size - hs.y : hs.y;
//     icon.anchor = new maps.Point(anchorX, size - anchorYFromBottom);
//   }

//   return icon;
// }
function buildMarkerIcon(maps: any, style: KmlStyle | null): any {
  const iconStyle = style && style.iconStyle;

  if (!iconStyle || !iconStyle.iconHref) {
    return undefined;
  }

  const baseSize = 32;
  const scale = iconStyle.scale || 1;
  const size = Math.max(1, Math.round(baseSize * scale));

  const icon: any = {
    url: iconStyle.iconHref,
    scaledSize: new maps.Size(size, size),

    // Position label ABOVE the marker
    labelOrigin: new maps.Point(size / 2, -8),
  };

  if (iconStyle.hotSpot) {
    const hs = iconStyle.hotSpot;

    const anchorX =
      hs.xunits === "fraction"
        ? hs.x * size
        : hs.xunits === "insetPixels"
        ? size - hs.x
        : hs.x;

    const anchorYFromBottom =
      hs.yunits === "fraction"
        ? hs.y * size
        : hs.yunits === "insetPixels"
        ? size - hs.y
        : hs.y;

    icon.anchor = new maps.Point(
      anchorX,
      size - anchorYFromBottom
    );
  }

  return icon;
}

/** Resolves a <LabelStyle> + placemark name into a Maps JS Marker `label` option. */
function buildMarkerLabel(style: KmlStyle | null, name: string): any {
  if (!name) return undefined;

  const labelStyle = style?.labelStyle;

  const fontSize = `${Math.max(
    8,
    Math.round(12 * (labelStyle?.scale || 1))
  )}px`;

  return {
    text: name,
    color: "#FFFFFF",
    fontSize,
    fontWeight: "500",
  };
}

/**
 * Renders a single parsed KML geometry (Point / LineString / Polygon /
 * MultiGeometry) as native Maps JS API object(s), applying the feature's
 * resolved style. Pushes every created object into `overlaysRef.current` so
 * it can be cleaned up later.
 */
export function renderKmlGeometry(
  maps: any,
  map: any,
  geometry: KmlGeometry | null,
  style: KmlStyle | null,
  properties: KmlPlacemarkProperties,
  overlaysRef: MutableRef<any[]>,
  infoWindowRef: MutableRef<any>
): void {
  if (!geometry) return;

  switch (geometry.type) {
    case "Point": {
      const marker = new maps.Marker({
        position: { lat: geometry.coordinates.lat, lng: geometry.coordinates.lng },
        map,
        title: properties ? properties.name : undefined,
        icon: buildMarkerIcon(maps, style),
        label: buildMarkerLabel(style, properties ? properties.name : ""),
      });

      const balloonHtml = properties ? buildBalloonHtml(properties, style) : null;
      if (balloonHtml) {
        marker.addListener("click", () => {
          if (!infoWindowRef.current) {
            infoWindowRef.current = new maps.InfoWindow();
          }
          infoWindowRef.current.setContent(balloonHtml);
          infoWindowRef.current.open(map, marker);
        });
      }

      overlaysRef.current.push(marker);
      break;
    }

    case "LineString":
    case "LinearRing": {
      // Standalone LinearRings (outside a Polygon) render as closed lines.
      const { hex, opacity } = splitKmlColor(
        style && style.lineStyle ? style.lineStyle.color : null,
        "#FF0000",
        1
      );
      const polyline = new maps.Polyline({
        path: toLatLngLiterals(geometry.coordinates),
        map,
        strokeColor: hex,
        strokeOpacity: opacity,
        strokeWeight: style && style.lineStyle ? style.lineStyle.width : 2,
      });
      overlaysRef.current.push(polyline);
      break;
    }

    case "Polygon": {
      const strokeColorInfo = splitKmlColor(
        style && style.lineStyle ? style.lineStyle.color : null,
        "#FF0000",
        1
      );
      const fillColorInfo = splitKmlColor(
        style && style.polyStyle ? style.polyStyle.color : null,
        "#FF0000",
        0.35
      );
      const fillEnabled = !style || !style.polyStyle || style.polyStyle.fill;
      const outlineEnabled = !style || !style.polyStyle || style.polyStyle.outline;

      const paths = [toLatLngLiterals(geometry.outerBoundary)];
      geometry.innerBoundaries.forEach((hole) => paths.push(toLatLngLiterals(hole)));

      const polygon = new maps.Polygon({
        paths,
        map,
        strokeColor: strokeColorInfo.hex,
        strokeOpacity: outlineEnabled ? strokeColorInfo.opacity : 0,
        strokeWeight: style && style.lineStyle ? style.lineStyle.width : 2,
        fillColor: fillColorInfo.hex,
        fillOpacity: fillEnabled ? fillColorInfo.opacity : 0,
      });
      overlaysRef.current.push(polygon);
      break;
    }

    case "MultiGeometry": {
      geometry.geometries.forEach((childGeometry) =>
        renderKmlGeometry(maps, map, childGeometry, style, properties, overlaysRef, infoWindowRef)
      );
      break;
    }

    default:
      break;
  }
}

/** Renders a parsed GroundOverlay as a native maps.GroundOverlay. */
function renderKmlGroundOverlay(maps: any, map: any, overlay: KmlGroundOverlay, overlaysRef: MutableRef<any[]>): void {
  if (!overlay.icon.href) return;
  const bounds = new maps.LatLngBounds(
    { lat: overlay.latLonBox.south, lng: overlay.latLonBox.west },
    { lat: overlay.latLonBox.north, lng: overlay.latLonBox.east }
  );
  const groundOverlay = new maps.GroundOverlay(overlay.icon.href, bounds, { map });
  overlaysRef.current.push(groundOverlay);
}

/**
 * Renders a parsed ScreenOverlay (a fixed-position image over the map, e.g. a
 * legend) via a custom google.maps.OverlayView anchored to the map's own div.
 * screenXY/overlayXY/size resolution follows the KML spec's bottom-left
 * screen-coordinate origin; `size` axes of 0 fall back to preserving aspect
 * ratio and negative/absent axes fall back to the image's natural size.
 */
function renderKmlScreenOverlay(maps: any, map: any, overlay: KmlScreenOverlay, overlaysRef: MutableRef<any[]>): void {
  if (!overlay.icon.href) return;

  function KmlScreenOverlayView(this: any) {}
  KmlScreenOverlayView.prototype = Object.create(maps.OverlayView.prototype);
  const instance: any = new (KmlScreenOverlayView as any)();

  let imgEl: HTMLImageElement | null = null;

  const axisPixels = (
    vec: KmlScreenOverlay["screenXY"],
    axis: "x" | "y",
    containerSize: number,
    defaultValue: number
  ): number => {
    const value = vec ? (axis === "x" ? vec.x : vec.y) : defaultValue;
    const units = vec ? (axis === "x" ? vec.xunits : vec.yunits) : "fraction";
    if (units === "pixels") return value;
    if (units === "insetPixels") return containerSize - value;
    return value * containerSize; // fraction
  };

  const reposition = () => {
    if (!imgEl) return;
    const mapDiv = map.getDiv();
    const containerWidth = mapDiv.clientWidth;
    const containerHeight = mapDiv.clientHeight;

    const naturalWidth = imgEl.naturalWidth || 1;
    const naturalHeight = imgEl.naturalHeight || 1;

    let width = naturalWidth;
    let height = naturalHeight;
    const size = overlay.size;
    if (size) {
      if (size.x > 0) width = size.xunits === "fraction" ? size.x * containerWidth : size.x;
      if (size.y > 0) height = size.yunits === "fraction" ? size.y * containerHeight : size.y;
      if (size.x > 0 && !(size.y > 0)) height = width * (naturalHeight / naturalWidth);
      if (size.y > 0 && !(size.x > 0)) width = height * (naturalWidth / naturalHeight);
    }

    const screenFromLeft = axisPixels(overlay.screenXY, "x", containerWidth, 0);
    const screenFromBottom = axisPixels(overlay.screenXY, "y", containerHeight, 0);
    const screenFromTop = containerHeight - screenFromBottom;

    const overlayFromLeft = axisPixels(overlay.overlayXY, "x", width, 0);
    const overlayFromBottom = axisPixels(overlay.overlayXY, "y", height, 0);
    const overlayFromTop = height - overlayFromBottom;

    imgEl.style.width = `${width}px`;
    imgEl.style.height = `${height}px`;
    imgEl.style.left = `${screenFromLeft - overlayFromLeft}px`;
    imgEl.style.top = `${screenFromTop - overlayFromTop}px`;
    if (overlay.rotation) {
      imgEl.style.transform = `rotate(${-overlay.rotation}deg)`;
    }
  };

  instance.onAdd = function () {
    imgEl = document.createElement("img");
    imgEl.src = overlay.icon.href as string;
    imgEl.style.position = "absolute";
    imgEl.style.pointerEvents = "none";
    imgEl.draggable = false;
    imgEl.onload = reposition;
    this.getPanes().floatPane.appendChild(imgEl);
  };

  instance.draw = reposition;

  instance.onRemove = function () {
    if (imgEl && imgEl.parentNode) imgEl.parentNode.removeChild(imgEl);
    imgEl = null;
  };

  instance.setMap(map);
  overlaysRef.current.push(instance);
}

/**
 * Renders a parsed PhotoOverlay. The Maps JS API has no native "photo viewer"
 * concept (that's an Earth-only feature), so this is approximated as a
 * clickable marker (when the PhotoOverlay has an anchoring <Point>) that
 * opens the photo in an InfoWindow. PhotoOverlays without a Point can't be
 * placed on a 2D map and are skipped (documented limitation).
 */
function renderKmlPhotoOverlay(
  maps: any,
  map: any,
  overlay: KmlPhotoOverlay,
  overlaysRef: MutableRef<any[]>,
  infoWindowRef: MutableRef<any>
): void {
  if (!overlay.point || !overlay.icon.href) return;

  const marker = new maps.Marker({
    position: { lat: overlay.point.coordinates.lat, lng: overlay.point.coordinates.lng },
    map,
    title: overlay.name || undefined,
    icon: { url: overlay.icon.href, scaledSize: new maps.Size(32, 32) },
  });

  marker.addListener("click", () => {
    if (!infoWindowRef.current) {
      infoWindowRef.current = new maps.InfoWindow();
    }
    infoWindowRef.current.setContent(
      `<div><img src="${overlay.icon.href}" style="max-width:400px;max-height:400px;display:block;" />` +
        `<strong>${overlay.name || ""}</strong><div>${overlay.description || ""}</div></div>`
    );
    infoWindowRef.current.open(map, marker);
  });

  overlaysRef.current.push(marker);
}

/** Dispatches a parsed overlay to its dedicated renderer based on `type`. */
function renderKmlOverlay(
  maps: any,
  map: any,
  overlay: KmlOverlay,
  overlaysRef: MutableRef<any[]>,
  infoWindowRef: MutableRef<any>
): void {
  if (!overlay.visibility) return;
  switch (overlay.type) {
    case "GroundOverlay":
      renderKmlGroundOverlay(maps, map, overlay, overlaysRef);
      break;
    case "ScreenOverlay":
      renderKmlScreenOverlay(maps, map, overlay, overlaysRef);
      break;
    case "PhotoOverlay":
      renderKmlPhotoOverlay(maps, map, overlay, overlaysRef, infoWindowRef);
      break;
    default:
      break;
  }
}

/**
 * Renders every Placemark and Overlay in a parsed folder, then recurses into
 * subfolders. Overlays nested inside folders are rendered here too (this was
 * previously missing, which silently dropped any GroundOverlay/ScreenOverlay/
 * PhotoOverlay placed inside a <Folder> instead of directly under <Document>).
 */
export function renderKmlFolder(
  maps: any,
  map: any,
  folder: KmlFolder,
  overlaysRef: MutableRef<any[]>,
  infoWindowRef: MutableRef<any>
): void {
  if (!folder.visibility) return;
  folder.placemarks.forEach((placemark) => {
    if (!placemark.properties.visibility) return;
    renderKmlGeometry(maps, map, placemark.geometry, placemark.style, placemark.properties, overlaysRef, infoWindowRef);
  });
  folder.overlays.forEach((overlay) => renderKmlOverlay(maps, map, overlay, overlaysRef, infoWindowRef));
  folder.folders.forEach((childFolder) => renderKmlFolder(maps, map, childFolder, overlaysRef, infoWindowRef));
}

/** Removes every Maps JS API object created for the currently rendered KML. */
export function clearKmlOverlays(overlaysRef: MutableRef<any[]>, infoWindowRef: MutableRef<any>): void {
  overlaysRef.current.forEach((overlay) => {
    if (overlay && typeof overlay.setMap === "function") {
      overlay.setMap(null);
    }
  });
  overlaysRef.current = [];
  if (infoWindowRef.current) {
    infoWindowRef.current.close();
  }
}

/**
 * Renders a full parsed KML document/result onto the map: root placemarks,
 * root folders (recursively, including their overlays), and root overlays.
 * Single entry point shared by every screen that needs to draw a KML file.
 */
export function renderParsedKml(
  maps: any,
  map: any,
  parsed: KmlParseResult,
  overlaysRef: MutableRef<any[]>,
  infoWindowRef: MutableRef<any>
): void {
  parsed.placemarks.forEach((placemark) => {
    if (!placemark.properties.visibility) return;
    renderKmlGeometry(maps, map, placemark.geometry, placemark.style, placemark.properties, overlaysRef, infoWindowRef);
  });

  parsed.folders.forEach((folder) => renderKmlFolder(maps, map, folder, overlaysRef, infoWindowRef));

  parsed.overlays.forEach((overlay) => renderKmlOverlay(maps, map, overlay, overlaysRef, infoWindowRef));
}
